import { DropdownInput, IntegrationData, MountingID, NativeMountingSystemsType, TextFieldInput } from 'types/mounting'
import { parseIntegrationJson } from 'util/misc'
import { parseVanDerValkModuleLayout } from '../vandervalk/applyVanDerValkSuitabilityResultToSystem'
import {
  getSolarRampFastenerTypeLabelKey,
  getSolarRampFastenerTypeOptions,
  getSolarRampFoundationTypeLabelKey,
  getSolarRampRoofConstructionHeightLabel,
  getSolarRampRoofConstructionHeightOptions,
  getSolarRampRoofMaterialLabelKey,
  getSolarRampRoofMaterialOptions,
  SOLAR_RAMP_ANCHOR_TYPE_OPTIONS,
  SOLAR_RAMP_FIXATION_OPTIONS,
} from '../vandervalk/solarRampAnchorFields'
import {
  DEFAULT_SOLAR_RAMP_FIXATION,
  DEFAULT_SOLAR_RAMP_FOUNDATION_TYPE,
  getSolarRampProfile,
  isVanDerValkSolarRampSystem,
  SOLAR_RAMP_MIDDLE_ZONE_DEFLECTOR_OPTIONS,
} from '../vandervalk/solarRampProfiles'
import { isVanDerValkSuitabilityConfigRow, type VanDerValkSuitabilityConfig } from '../vandervalk/types'
import {
  createDlubalSnowButtonForSolarNest,
  createDlubalUKDistanceToShorelineButton,
  createDlubalUKSnowElevationAltitudeButton,
  createDlubalUKSnowZoneButtonForVanDerValk,
  createDlubalUKWindSpeedMsButton,
  createDlubalWindButtonForSolarNest,
} from './dlubalButtonConfigs'
import { FilterCriteria, filterOptions, RoofTypeCompatibility } from './inputFilters'
import { getRoofHookCalculationMethodInputs } from './Inputs'
import { SolarRoofPro } from './mounting_systems/ClenergySolarRoofPro'
import { getSolarRoofProRoofHookDropdowns } from './mounting_systems/ClenergySolarRoofProInputs'
import { ClickFit, FlatFix } from './mounting_systems/Esdec/Esdec'
import {
  Fastensol,
  FASTENSOL_END_CAP_CODES,
  FASTENSOL_END_CAP_LABELS,
  FASTENSOL_END_CAP_VARIABLE_NAME,
  FASTENSOL_END_CLAMP_CODES,
  FASTENSOL_END_CLAMP_LABELS,
  FASTENSOL_END_CLAMP_VARIABLE_NAME,
  FASTENSOL_MID_CLAMP_CODES,
  FASTENSOL_MID_CLAMP_LABELS,
  FASTENSOL_MID_CLAMP_VARIABLE_NAME,
  FASTENSOL_RAIL_CODES,
  FASTENSOL_RAIL_LABELS,
  FASTENSOL_RAIL_VARIABLE_NAME,
  FASTENSOL_SPLICE_CODES,
  FASTENSOL_SPLICE_LABELS,
  FASTENSOL_SPLICE_VARIABLE_NAME,
  getFastensolRoofHookInputs,
} from './mounting_systems/FastensolPitched'
import { GseInRoof } from './mounting_systems/GSE/GseInRoof'
import { K2OnRoofUK } from './mounting_systems/K2UK/K2uk'
import { createK2UKInputs } from './mounting_systems/K2UK/K2UKInputForm'
import { getDlubalCache } from '../dlubal'
import { createK2Inputs } from './mounting_systems/K2US/K2InputForm'
import { K2GroundMount, K2OnRoof, K2SimpleTilt } from './mounting_systems/K2US/K2us'
import { MSPitchedProLine } from './mounting_systems/MSPitchedProLine/MSPitchedProLine'
import {
  gmbhDropdowns,
  compatibilityRules as msPitchedProlineCompatibilityRules,
} from './mounting_systems/MSPitchedProLine/MSPitchedProLine_products'
import { MSTrapezeProLine } from './mounting_systems/MSTrapezeProLine/MSTrapezeProLine'
import { Nova } from './mounting_systems/Nova/Nova'
import { createNovaInputs } from './mounting_systems/Nova/NovaInputForm'
import { Metasole } from './mounting_systems/RenusolMetasole'
import { Variosole } from './mounting_systems/RenusolVariosole'
import {
  allSchletterInputs,
  Schletter,
  compatibilityRules as schletterCompatibilityRules,
} from './mounting_systems/SchletterOnRoof'
import { SnapNRackUltraRail } from './mounting_systems/SnapNRack/UltraRail'
import { createSnapnrackInputs } from './mounting_systems/SnapNRack/SnapnrackInputForm'
import { SolarNestGroundMount } from './mounting_systems/SolarNestGroundMount'
import { SolarNestRooftop } from './mounting_systems/SolarNestRooftop'
import {
  getSolarNestMaxWindSnow,
  SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES,
} from './mounting_systems/solarNestWindSnowMax'
import { SolarPortGroundMount } from './mounting_systems/SolarPort/SolarPort'
import { createSolarPortInputs } from './mounting_systems/SolarPort/SolarPortInputForm'
import { FastRack } from './mounting_systems/FastRack/FastRack'
import { createFastRackInputs } from './mounting_systems/FastRack/FastRackInputForm'
import { SunmodoNanoRack } from './mounting_systems/SunmodoNanoRack'
import { SunmodoSMRPitched } from './mounting_systems/SunmodoSMRPitched'
import { getDlubalUKDistanceToSeaCache, getDlubalUKWindCache } from './mounting_systems/ukDlubal'
import { VanDerValk } from './mounting_systems/VanDerValk'
import { getPanelOrientationsInSystem, getRailDirectionInputs, getRailOrientationsInSystem } from './railing'
import { getRoofName } from './utils'

type IncompatibleMountingSystemsType = {
  [feature in 'multiple-panels']: MountingID[]
}
export const INCOMPATIBLE_MOUNTING_SYSTEMS: IncompatibleMountingSystemsType = {
  'multiple-panels': ['ironridge', 'renusol'],
}

export function getIncompatibleMountingSystems(): MountingID[] {
  let incompatibleSystems: MountingID[] = []
  if (window.editor.selectedSystem.hasMultipleModuleTypes()) {
    incompatibleSystems.push(...INCOMPATIBLE_MOUNTING_SYSTEMS['multiple-panels'])
  }
  return incompatibleSystems
}

export function getNativeMountingSystems(): NativeMountingSystemsType {
  const roofTypeName = getRoofName(window.projectForm.getState().values.roof_type)

  return {
    sunmodoSMRPitched: {
      class: SunmodoSMRPitched,
      manufacturer: 'Sunmodo',
      getInputs: (projectForm, system) => {
        const integrationData = system.integration_json?.sunmodoSMRPitched as IntegrationData
        const roofHookInputs = getRoofHookCalculationMethodInputs(system)
        const inputs = [
          ...(integrationData.mountType &&
          ['fiveTopTile', 'sevenTopTile', 'mrbMount'].includes(integrationData.mountType)
            ? []
            : [
                {
                  label: 'What will you attach to?',
                  variableName: 'attachmentType',
                  default: 'rafters',
                  options: [
                    { label: 'Rafters', value: 'rafters' },
                    { label: 'Decking', value: 'decking' },
                  ],
                },
              ]),
          {
            label: 'Mount',
            variableName: 'mountType',
            default: 'nanoRafterMount',
            options: [
              { label: 'Nano Rafter Mount', value: 'nanoRafterMount' },
              { label: 'NanoBit', value: 'nanoBit' },
              { label: 'SoloFlash', value: 'soloFlash' },
              { label: 'Nano Deck Mount', value: 'nanoDeckMount' },
              { label: '5" TopTile', value: 'fiveTopTile' },
              { label: '7" TopTile', value: 'sevenTopTile' },
              { label: 'MRB Mount', value: 'mrbMount' },
            ],
          },
          {
            label: 'Colour',
            variableName: 'colour',
            default: 'black',
            options: [
              { label: 'Black', value: 'black' },
              { label: 'Silver', value: 'silver' },
            ],
          },
          {
            label: 'End clamp type',
            variableName: 'endClamp',
            default: 'standard',
            options: [
              { label: 'Standard end clamp', value: 'standard' },
              { label: 'Bottom end clamp', value: 'bottom' },
            ],
          },
          {
            label: 'Include end caps?',
            variableName: 'endCap',
            default: true,
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
          },
          {
            label: 'Wire management clip quantity',
            variableName: 'wireClipQty',
            default: 1,
            options: [
              { label: '1 / module', value: 1 },
              { label: '2 / module', value: 2 },
              { label: 'None', value: 0 },
            ],
          },
          {
            label: 'Include MLPE mount?',
            variableName: 'mlpeMount',
            default: true,
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
          },
          {
            label: 'Conduit mount',
            variableName: 'conduitMount',
            default: false,
            options: [
              { label: 'None', value: false },
              { label: '3/4" conduit mount', value: 'CLMP-CON-075-S' },
              { label: '1" conduit mount', value: 'CLMP-CON-100-S' },
            ],
          },
        ] as DropdownInput[]

        const compatibilityRules = {
          attachmentType: {
            roofTypeCompatibility: {
              rafters: ['Composition / Asphalt Shingle'],
              decking: ['Composition / Asphalt Shingle'],
            } as RoofTypeCompatibility,
          },
          mountType: {
            roofTypeCompatibility: {
              nanoRafterMount: ['Composition / Asphalt Shingle'],
              nanoBit: ['Composition / Asphalt Shingle', 'Trapezoidal', 'Metal Standing Seam'],
              soloFlash: ['Composition / Asphalt Shingle'],
              nanoDeckMount: ['Composition / Asphalt Shingle'],
              fiveTopTile: ['Tile Clay', 'Tile Concrete', 'Tile Slate'],
              sevenTopTile: ['Tile Clay', 'Tile Concrete', 'Tile Slate'],
              mrbMount: ['Trapezoidal', 'Metal Standing Seam'],
            } as RoofTypeCompatibility,
            attachmentTypeCompatibility: {
              nanoRafterMount: ['rafters'],
              nanoBit: ['rafters'],
              soloFlash: ['rafters'],
              nanoDeckMount: ['decking'],
            },
          },
        }
        const textFieldInputs = [
          ...(integrationData.conduitMount
            ? [
                {
                  label: 'Conduit mount quantity',
                  variableName: 'conduitMountQty',
                  default: 0,
                  type: 'number',
                  min: 0,
                },
              ]
            : []),
        ] as TextFieldInput[]

        const filterCriteria = [
          {
            filterType: 'roofTypeCompatibility',
            filterValue: roofTypeName,
          },
          {
            filterType: 'attachmentTypeCompatibility',
            filterValue: '#/attachmentType',
          },
        ] as FilterCriteria[]

        // Clear attachment type when the input is hidden to ensure the compatibility filtering works
        if (
          integrationData.mountType &&
          ['fiveTopTile', 'sevenTopTile', 'mrbMount'].includes(integrationData.mountType)
        )
          delete integrationData.attachmentType

        const inputsToDisplay = filterOptions(inputs, compatibilityRules, filterCriteria, integrationData)

        return {
          dropdownInputs: [...inputsToDisplay, ...(roofHookInputs.dropdownInputs ? roofHookInputs.dropdownInputs : [])],
          textFieldInputs: [
            ...textFieldInputs,
            ...(roofHookInputs.textFieldInputs ? roofHookInputs.textFieldInputs : []),
          ],
        }
      },
    },
    sunmodoNanoRack: {
      class: SunmodoNanoRack,
      manufacturer: 'Sunmodo',
      getInputs: (projectForm, system) => {
        const integrationData = system.integration_json?.sunmodoNanoRack as IntegrationData
        const projectData = projectForm.getState().values
        const roofTypeName = getRoofName(projectData.roof_type)
        const roofHookInputs = getRoofHookCalculationMethodInputs(system)
        const inputs: DropdownInput[] = [
          {
            label: 'Skirt configuration',
            variableName: 'skirtConfiguration',
            default: 'frontOnly',
            options: [
              { label: 'No Skirt', value: 'none' },
              { label: 'Front Skirt Only', value: 'frontOnly' },
              { label: 'Front & Side Skirt', value: 'frontAndSide' },
              { label: '360 Skirt', value: 'all' },
            ],
          },
          {
            label: 'Include MLPE mount?',
            variableName: 'mlpeMount',
            default: true,
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
          },
          {
            label: 'Conduit mount',
            variableName: 'conduitMount',
            default: false,
            options: [
              { label: 'None', value: false },
              { label: '3/4" conduit mount', value: 'CLMP-CON-075-S' },
              { label: '1" conduit mount', value: 'CLMP-CON-100-S' },
            ],
          },
          {
            label: 'Clamp placement',
            variableName: 'clampPlacement',
            default: 'long',
            options: [
              { label: 'Long side', value: 'long' },
              { label: 'Short side', value: 'short' },
            ],
          },
        ]

        return {
          dropdownInputs: [...inputs, ...(roofHookInputs.dropdownInputs ? roofHookInputs.dropdownInputs : [])],
          textFieldInputs: [],
        }
      },
    },
    solarRoofPro: {
      class: SolarRoofPro,
      manufacturer: 'Clenergy',
      getInputs: (projectForm, system) => {
        const roofHookInputs = getRoofHookCalculationMethodInputs(system)
        const railInputs = getRailDirectionInputs(system)
        const currentRailLength = system?.integration_json?.solarRoofPro?.railLength
        const showRailColour = currentRailLength === 3600
        return {
          dropdownInputs: [
            ...(railInputs.dropdownInputs ? railInputs.dropdownInputs : []),
            {
              label: 'Rail Length (mm)',
              variableName: 'railLength',
              default: 3600,
              options: [
                { label: 3500, value: 3500 },
                { label: 3600, value: 3600 },
                { label: 3800, value: 3800 },
                { label: 4600, value: 4600 },
              ],
            },
            ...(showRailColour
              ? [
                  {
                    label: 'Rail colour',
                    variableName: 'railColour',
                    default: 'silver',
                    options: [
                      { label: 'Silver', value: 'silver' },
                      { label: 'Black', value: 'black' },
                    ],
                  },
                ]
              : []),
            {
              label: 'Clamp Colour',
              variableName: 'clampColour',
              default: 'black',
              options: [
                { label: 'Black', value: 'black' },
                { label: 'Silver', value: 'silver' },
              ],
            },
            {
              label: 'End Cap Colour',
              variableName: 'endCapColour',
              default: 'black',
              options: [
                { label: 'Black', value: 'black' },
                { label: 'Grey', value: 'grey' },
              ],
            },
            ...getSolarRoofProRoofHookDropdowns(system, roofTypeName),
            ...(roofHookInputs.dropdownInputs ? roofHookInputs.dropdownInputs : []),
          ],
          textFieldInputs: roofHookInputs.textFieldInputs ? roofHookInputs.textFieldInputs : [],
        }
      },
    },
    solarNestRooftop: {
      class: SolarNestRooftop,
      manufacturer: 'SolarNEST',
      getInputs: (projectForm, system) => {
        const windCache = getDlubalCache('wind')
        const snowCache = getDlubalCache('snow')
        const maxes = getSolarNestMaxWindSnow(system)
        // Use MOST_CONSERVATIVE values (102/10) when Dlubal is not available
        // Also use MOST_CONSERVATIVE if Dlubal cache exists but values don't match table (e.g., cached value 2 is below table minimum 10)
        const windCacheValid =
          windCache?.success && windCache?.value != null && maxes && windCache.value <= maxes.maxWindMph
        const snowCacheValid =
          snowCache?.success && snowCache?.value != null && maxes && snowCache.value <= maxes.maxSnowPsf
        const useMostConservative = (!windCache?.success || !windCacheValid) && (!snowCache?.success || !snowCacheValid)
        const windDefault = useMostConservative ? 102 : ''
        const snowDefault = useMostConservative ? 10 : ''
        return {
          dropdownInputs: [
            {
              label: 'Tilt options',
              variableName: 'tiltOption',
              default: 'eastWestTilt',
              options: [
                { label: 'East/West tilt', value: 'eastWestTilt' },
                { label: 'Triple Tilt (E-W-S)', value: 'tripleTilt' },
              ],
            },
          ],
          textFieldInputs: [
            {
              label: 'Wind Speed (mph)',
              variableName: 'windSpeed',
              type: 'number',
              default: windDefault,
              button: createDlubalWindButtonForSolarNest(system),
            },
            {
              label: 'Design Snow Load (psf)',
              variableName: 'designSnowLoad',
              type: 'number',
              default: snowDefault,
              button: createDlubalSnowButtonForSolarNest(system),
            },
          ],
        }
      },
    },
    solarNestGroundMount: {
      class: SolarNestGroundMount,
      manufacturer: 'SolarNEST',
      getInputs: (projectForm, system) => {
        const windCache = getDlubalCache('wind')
        const snowCache = getDlubalCache('snow')
        const maxes = getSolarNestMaxWindSnow(system)
        // Use MOST_CONSERVATIVE values (102/10) when Dlubal is not available
        // Also use MOST_CONSERVATIVE if Dlubal cache exists but values don't match table (e.g., cached value 2 is below table minimum 10)
        const windCacheValid =
          windCache?.success && windCache?.value != null && maxes && windCache.value <= maxes.maxWindMph
        const snowCacheValid =
          snowCache?.success && snowCache?.value != null && maxes && snowCache.value <= maxes.maxSnowPsf
        const useMostConservative = (!windCache?.success || !windCacheValid) && (!snowCache?.success || !snowCacheValid)
        const windDefault = useMostConservative ? 102 : ''
        const snowDefault = useMostConservative ? 10 : ''
        return {
          dropdownInputs: [
            {
              label: 'Tilt options',
              variableName: 'tiltOption',
              default: 'eastWestTilt',
              options: [
                { label: 'East/West tilt', value: 'eastWestTilt' },
                { label: 'Triple Tilt (E-W-S)', value: 'tripleTilt' },
              ],
            },
            {
              label: 'Soil type',
              variableName: 'soilType',
              default: 'Unknown',
              options: [
                { label: 'Dense Clay', value: 'Dense Clay' },
                { label: 'Fine Sand', value: 'Fine Sand' },
                { label: 'Sandy Loam', value: 'Sandy Loam' },
                { label: 'Loamy', value: 'Loamy' },
                { label: 'Hard Silts/Clays', value: 'Hard Silts/Clays' },
                { label: 'Rocky Conditions', value: 'Rocky Conditions' },
                { label: 'Bedrock', value: 'Bedrock' },
                { label: 'Unknown', value: 'Unknown' },
              ],
            },
            {
              label: 'Frost line depth',
              variableName: 'frostLineDepth',
              default: 'Less than 36"',
              options: [
                { label: 'Greater than 36"', value: 'Greater than 36"' },
                { label: 'Less than 36"', value: 'Less than 36"' },
              ],
            },
          ],
          textFieldInputs: [
            {
              label: 'Ground clearance (inches)',
              variableName: 'groundClearance',
              type: 'number',
              default: SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES,
            },
            {
              label: 'Ground slope (degrees)',
              variableName: 'groundSlope',
              type: 'number',
              default: 0,
              min: 0,
            },
            {
              label: 'Wind Speed (mph)',
              variableName: 'windSpeed',
              type: 'number',
              default: windDefault,
              button: createDlubalWindButtonForSolarNest(system),
            },
            {
              label: 'Design Snow Load (psf)',
              variableName: 'designSnowLoad',
              type: 'number',
              default: snowDefault,
              button: createDlubalSnowButtonForSolarNest(system),
            },
          ],
        }
      },
    },
    variosole: {
      class: Variosole,
      manufacturer: 'Renusol',
      getInputs: (projectForm, system) => {
        const roofHookInputs = getRoofHookCalculationMethodInputs(system)
        const railInputs = getRailDirectionInputs(system)

        var options = [
          { label: '420172 RH Flat Roof Hook', value: '420172' },
          { label: '420171 RH1 Roof Hook', value: '420171' },
          { label: '420182 UK Flexible Roof Hook', value: '420182' },
          { label: '420183 UK Flexible 90 degrees Roof Hook', value: '420183' },
        ]

        if (roofTypeName) {
          if (roofTypeName === 'Tile Clay') {
            options.push(
              ...[
                { label: '420150 Stainless Steel Roof Hook', value: '420150' },
                { label: '420151 Aluminium Roof Hook', value: '420151' },
                { label: '420180 UK Pantiles Roof Hook', value: '420180' },
              ]
            )
          } else if (roofTypeName === 'Tile Concrete') {
            options.push(
              ...[
                { label: '420153 Plain Tiles Roof Hook', value: '420153' },
                { label: '420180 UK Pantiles Roof Hook', value: '420180' },
                { label: '420181 UK Plain Tiles / Slates Roof Hook', value: '420181' },
                { label: '420184 High Load UK Plain Tiles / Slates Roof Hook', value: '420184' },
              ]
            )
          } else if (roofTypeName === 'Tile Slate') {
            options.push(
              ...[
                { label: '420155 Slate Tiles Roof Hook', value: '420155' },
                { label: '420181 UK Plain Tiles / Slates Roof Hook', value: '420181' },
                { label: '420184 High Load UK Plain Tiles / Slates Roof Hook', value: '420184' },
              ]
            )
          }
        }

        return {
          dropdownInputs: [
            ...(railInputs.dropdownInputs ? railInputs.dropdownInputs : []),
            {
              label: 'Roof Hook',
              variableName: 'roofHook',
              default: '420172',
              options,
            },
            {
              label: 'Rail Length (mm)',
              variableName: 'railLength',
              default: 3600,
              options: [
                { label: 3600, value: 3600 },
                { label: 4400, value: 4400 },
              ],
            },
            {
              label: 'Rail Colour',
              variableName: 'railColour',
              default: 'black',
              options: [
                { label: 'Silver', value: 'silver' },
                { label: 'Black', value: 'black' },
              ],
            },
            {
              label: 'Clamp Colour',
              variableName: 'clampColour',
              default: 'black',
              options: [
                { label: 'Silver', value: 'silver' },
                { label: 'Black', value: 'black' },
              ],
            },
            {
              label: 'Genius Solution',
              variableName: 'geniusKit',
              default: 'None',
              options: [
                { label: 'None', value: 'None' },
                { label: 'Flat Tiled Kit', value: 'SFS003' },
                { label: 'Slate Refit Kit', value: 'SKI001' },
                { label: 'Slate Kit for New Build', value: 'SFS001' },
              ],
              // Note: The Genius refit kits are compatible with most roof anchors but be aware a 30mm step up distance is required from the anchor fixing plate and the arm of the roof anchor. Generally this means anchors designed for plain tiles are most suited and certain products are not compatible. Non compatible components are: SCH-256313, SCH-257125, SCH-256315, SLT-102001-000, SLT-102001-001, REN-920005..
            },
            ...(roofHookInputs.dropdownInputs ? roofHookInputs.dropdownInputs : []),
          ],
          textFieldInputs: roofHookInputs.textFieldInputs ? roofHookInputs.textFieldInputs : [],
        }
      },
    },
    schletter: {
      class: Schletter,
      manufacturer: 'Schletter',
      getInputs: (projectForm, system) => {
        const roofHookInputs = getRoofHookCalculationMethodInputs(system)
        const railInputs = getRailDirectionInputs(system)

        const integrationData = system.integration_json?.schletter as IntegrationData

        const filterCriteria = [
          {
            filterType: 'roofTypeCompatibility',
            filterValue: roofTypeName,
          },
          {
            filterType: 'railCompatibility',
            filterValue: '#/mountingRail',
          },
          {
            filterType: 'roofHookMaterialCompatibility',
            filterValue: '#/roofHook',
          },
        ] as FilterCriteria[]

        const getDefaultEndCap = (mountingRail?: string): string => {
          const DEFAULT_END_CAP = '129011-900'
          const PRO_RAIL_END_CAP = '129200-910'

          if (!mountingRail) return DEFAULT_END_CAP

          const proRails = new Set([
            '120020-93600',
            '120020-04700',
            '120020-94700',
            '120021-04700',
            '120039-04400',
            '120039-04750',
            '120039-05900',
            '120039-06200',
          ])

          return proRails.has(mountingRail) ? PRO_RAIL_END_CAP : DEFAULT_END_CAP
        }

        const schletterInputsWithDynamicDefaults = allSchletterInputs.map((input) => {
          if (roofTypeName === 'Tile Slate' && input.variableName === 'roofHook') {
            return { ...input, default: '109019-024' }
          }
          if (input.variableName === 'endCap') {
            return {
              ...input,
              default: getDefaultEndCap(integrationData?.mountingRail),
            }
          }
          return input
        })

        var inputsToDisplay = filterOptions(
          schletterInputsWithDynamicDefaults,
          schletterCompatibilityRules,
          filterCriteria,
          integrationData
        )

        const railOrientationsInSystem = getRailOrientationsInSystem(system, integrationData)
        if (!railOrientationsInSystem.horizontal)
          inputsToDisplay = inputsToDisplay.filter((input) => input.variableName !== 'horizontalEndClamp')
        if (!railOrientationsInSystem.vertical)
          inputsToDisplay = inputsToDisplay.filter((input) => input.variableName !== 'verticalEndClamp')

        return {
          dropdownInputs: [
            ...(railInputs.dropdownInputs ? railInputs.dropdownInputs : []),
            ...inputsToDisplay,
            ...(roofHookInputs.dropdownInputs ? roofHookInputs.dropdownInputs : []),
          ],
          textFieldInputs: roofHookInputs.textFieldInputs ? roofHookInputs.textFieldInputs : [],
        }
      },
    },
    fastensol: {
      class: Fastensol,
      manufacturer: 'Fastensol',
      getInputs: (projectForm, system) => {
        const roofHookInputs = getRoofHookCalculationMethodInputs(system)
        const fastensolRoofHookInputs = getFastensolRoofHookInputs(system, roofTypeName)

        return {
          dropdownInputs: [
            {
              label: 'Railing',
              variableName: FASTENSOL_RAIL_VARIABLE_NAME,
              default: 'F-MR-S3550',
              options: FASTENSOL_RAIL_CODES.map((code) => ({
                label: FASTENSOL_RAIL_LABELS[code],
                value: code,
              })),
            },
            ...(fastensolRoofHookInputs.dropdownInputs ?? []),
            {
              label: 'End Cap',
              variableName: FASTENSOL_END_CAP_VARIABLE_NAME,
              default: 'F-REC-S',
              options: FASTENSOL_END_CAP_CODES.map((code) => ({
                label: FASTENSOL_END_CAP_LABELS[code],
                value: code,
              })),
            },
            {
              label: 'End Clamp',
              variableName: FASTENSOL_END_CLAMP_VARIABLE_NAME,
              default: 'F-UNIVERSAL-CLAMP',
              options: FASTENSOL_END_CLAMP_CODES.map((code) => ({
                label: FASTENSOL_END_CLAMP_LABELS[code],
                value: code,
              })),
            },
            {
              label: 'Mid Clamp',
              variableName: FASTENSOL_MID_CLAMP_VARIABLE_NAME,
              default: 'F-UNIVERSAL-CLAMP',
              options: FASTENSOL_MID_CLAMP_CODES.map((code) => ({
                label: FASTENSOL_MID_CLAMP_LABELS[code],
                value: code,
              })),
            },
            {
              label: 'Splice',
              variableName: FASTENSOL_SPLICE_VARIABLE_NAME,
              default: 'F-RC-S',
              options: FASTENSOL_SPLICE_CODES.map((code) => ({
                label: FASTENSOL_SPLICE_LABELS[code],
                value: code,
              })),
            },
          ],
          textFieldInputs: roofHookInputs.textFieldInputs ? roofHookInputs.textFieldInputs : [],
        }
      },
    },
    msPitchedProLine: {
      class: MSPitchedProLine,
      manufacturer: 'Mounting Systems GmbH',
      getInputs: (projectForm, system) => {
        const roofHookInputs = getRoofHookCalculationMethodInputs(system)

        const integrationData = system.integration_json?.msPitchedProLine as IntegrationData

        const filterCriteria = [
          {
            filterType: 'profileCompatibility',
            filterValue: '#/profile',
          },
          {
            filterType: 'roofHookMaterialCompatibility',
            filterValue: '#/roofHookMaterial',
          },
        ] as FilterCriteria[]

        var inputsToDisplay = filterOptions(
          gmbhDropdowns,
          msPitchedProlineCompatibilityRules,
          filterCriteria,
          integrationData
        )

        return {
          dropdownInputs: [
            {
              label: 'Clamp Colour',
              variableName: 'clampColour',
              default: 'black',
              options: [
                { label: 'Black', value: 'black' },
                { label: 'Silver', value: 'silver' },
              ],
            },
            {
              label: 'End Cap Colour',
              variableName: 'endCapColour',
              default: 'black',
              options: [
                { label: 'Black', value: 'black' },
                { label: 'Silver', value: 'silver' },
              ],
            },
            {
              label: 'Profile',
              variableName: 'profile',
              default: '4/35',
              options: [
                { label: '4/35', value: '4/35' },
                { label: '5/41', value: '5/41' },
                { label: '6/40', value: '6/40' },
                { label: '10/48', value: '10/48' },
              ],
            },
            {
              label: 'Roof Hook Material',
              variableName: 'roofHookMaterial',
              default: 'stainlessSteel',
              options: [
                { label: 'Stainless Steel', value: 'stainlessSteel' },
                { label: 'Aluminium', value: 'aluminium' },
              ],
            },
            ...inputsToDisplay,
          ],
          textFieldInputs: roofHookInputs.textFieldInputs ? roofHookInputs.textFieldInputs : [],
        }
      },
    },
    msTrapezeProLine: {
      class: MSTrapezeProLine,
      manufacturer: 'Mounting Systems GmbH',
      getInputs: (projectForm, system) => {
        const spacingDisabled = system.integration_json?.msTrapezeProLine?.mountingRail === '712-0252'
        const roofHookInputs = getRoofHookCalculationMethodInputs(system, spacingDisabled)
        return {
          dropdownInputs: [
            {
              variableName: 'mountingRail',
              label: 'Rail Type',
              default: '712-0252',
              options: [
                {
                  value: '712-0252',
                  label: 'Mini',
                },
                {
                  value: '712-0254',
                  label: 'Continuous',
                },
              ],
            },
          ],
          textFieldInputs: roofHookInputs.textFieldInputs ? roofHookInputs.textFieldInputs : [],
        }
      },
    },
    esdec: {
      class: ClickFit,
      manufacturer: 'ESDEC',
      getInputs: (projectForm, system) => {
        const integrationData = system.integration_json?.esdec as IntegrationData

        const inputs = [
          {
            label: 'Rail Direction',
            variableName: 'railDirection',
            default: 'horizontal',
            options: [
              { label: 'Horizontal', value: 'horizontal' },
              { label: 'Vertical', value: 'vertical' },
            ],
          },
          {
            label: 'Eurocode Terrain Category',
            variableName: 'terrainCategory',
            default: 'EU_CATEGORY_3',
            options: [
              { label: 'Category 0', value: 'EU_CATEGORY_0' },
              { label: 'Category 1', value: 'EU_CATEGORY_1' },
              { label: 'Category 2', value: 'EU_CATEGORY_2' },
              { label: 'Category 3', value: 'EU_CATEGORY_3' },
              { label: 'Category 4', value: 'EU_CATEGORY_4' },
            ],
          },
          ...(roofTypeName?.includes('Tile')
            ? [
                {
                  label: 'Roof Hook',
                  variableName: 'roofHook',
                  default: 'NORMAL',
                  options: [
                    { label: 'Normal', value: 'NORMAL' },
                    { label: 'Truss', value: 'TRUSS' },
                  ],
                },
              ]
            : []),
          ...(roofTypeName === 'Metal Standing Seam'
            ? [
                {
                  label: 'Seam Type',
                  variableName: 'seamType',
                  default: 'SNAP',
                  options: [
                    { label: 'Single Folded', value: 'SINGLE_FOLDED' },
                    { label: 'Double Folded', value: 'DOUBLE_FOLDED' },
                    { label: 'Rounded', value: 'ROUNDED' },
                    { label: 'Snap', value: 'SNAP' },
                  ],
                },
              ]
            : []),
          {
            label: 'Supply black parts if possible?',
            variableName: 'blackArticles',
            default: false,
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
          },
        ] as DropdownInput[]

        const compatibilityRules = {
          system: {
            roofTypeCompatibility: {
              CLICKFIT_EVO: ['Tile Clay', 'Tile Concrete', 'Tile Slate', 'Trapezoidal', 'Metal Standing Seam'],
              FLATFIX_FUSION: ['Tar and Gravel / Bitumen', 'Membrane TPO'],
            } as RoofTypeCompatibility,
          },
        }

        const filterCriteria = [
          {
            filterType: 'roofTypeCompatibility',
            filterValue: roofTypeName,
          },
        ] as FilterCriteria[]

        const inputsToDisplay = filterOptions(inputs, compatibilityRules, filterCriteria, integrationData)

        return {
          dropdownInputs: [...inputsToDisplay] as DropdownInput[],
          textFieldInputs: [
            ...(roofTypeName === 'Metal Standing Seam'
              ? [
                  {
                    label: 'Flange Distance (mm)',
                    variableName: 'flangeDistance',
                    type: 'number',
                    default: 400,
                  },
                ]
              : []),
            {
              label: 'Wind Speed (m/s)',
              variableName: 'windSpeed',
              type: 'number',
              default: 15,
            },
            {
              label: 'Roof Height (m)',
              variableName: 'roofHeight',
              type: 'number',
              default: 5,
              max: 20,
            },
          ] as TextFieldInput[],
        }
      },
    },
    flatFix: {
      class: FlatFix,
      manufacturer: 'ESDEC',
      getInputs: (projectForm, system) => {
        const integrationData = system.integration_json?.esdec as IntegrationData

        const inputs = [
          {
            label: 'Panel Configuration',
            variableName: 'panelConfiguration',
            default: 'SINGLE',
            options: [
              { label: 'Single', value: 'SINGLE' },
              // {label: 'Dual', value:'DUAL'} // Dual is an East/West system which we don't support yet
            ],
          },
          {
            label: 'Eurocode Terrain Category',
            variableName: 'terrainCategory',
            default: 'EU_CATEGORY_3',
            options: [
              { label: 'Category 0', value: 'EU_CATEGORY_0' },
              { label: 'Category 1', value: 'EU_CATEGORY_1' },
              { label: 'Category 2', value: 'EU_CATEGORY_2' },
              { label: 'Category 3', value: 'EU_CATEGORY_3' },
              { label: 'Category 4', value: 'EU_CATEGORY_4' },
            ],
          },
          {
            label: 'Row Distance',
            variableName: 'rowDistance',
            default: 'ROW_DISTANCE_1700',
            options: [
              { label: '1320', value: 'ROW_DISTANCE_1320' },
              { label: '1500', value: 'ROW_DISTANCE_1500' },
              { label: '1700', value: 'ROW_DISTANCE_1700' },
              { label: '1890', value: 'ROW_DISTANCE_1890' },
              // These options above are only compatible with SINGLE panelConfiguration, the options for DUAL panelConfiguration (not yet supported in OpenSolar) are below
              // { label: '2100', value: 'ROW_DISTANCE_2100' },
              // { label: '2260', value: 'ROW_DISTANCE_2260' },
              // { label: '2440', value: 'ROW_DISTANCE_2440' },
              // { label: '2640', value: 'ROW_DISTANCE_2640' },
              // { label: '2830', value: 'ROW_DISTANCE_2830' },
            ],
          },
          ...(roofTypeName?.includes('TPO')
            ? [
                {
                  label: 'Use TPO Mats?',
                  variableName: 'tpoMats',
                  default: false,
                  options: [
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ],
                },
              ]
            : []),
        ] as DropdownInput[]

        const compatibilityRules = {
          system: {
            roofTypeCompatibility: {
              CLICKFIT_EVO: ['Tile Clay', 'Tile Concrete', 'Tile Slate', 'Trapezoidal', 'Metal Standing Seam'],
              FLATFIX_FUSION: ['Tar and Gravel / Bitumen', 'Membrane TPO'],
            } as RoofTypeCompatibility,
          },
        }

        const filterCriteria = [
          {
            filterType: 'roofTypeCompatibility',
            filterValue: roofTypeName,
          },
        ] as FilterCriteria[]

        const inputsToDisplay = filterOptions(inputs, compatibilityRules, filterCriteria, integrationData)

        return {
          dropdownInputs: [...inputsToDisplay] as DropdownInput[],
          textFieldInputs: [
            {
              label: 'Wind Speed (m/s)',
              variableName: 'windSpeed',
              type: 'number',
              default: 15,
            },
            {
              label: 'Roof Height',
              variableName: 'roofHeight',
              type: 'number',
              default: 5,
            },
          ] as TextFieldInput[],
        }
      },
    },
    metasole: {
      class: Metasole,
      manufacturer: 'Renusol',
      getInputs: (projectForm, system) => {
        const panelOrientationsInSystem = getPanelOrientationsInSystem(system)

        const metasoleDropDownInputs = [] as DropdownInput[]
        const metasoleTextFieldInputs = [] as TextFieldInput[]

        if (panelOrientationsInSystem.landscape) {
          metasoleDropDownInputs.push({
            label: 'Include corrugated roof adapter for landscape panels? ',
            variableName: 'includeRoofAdapter',
            default: 'false',
            options: [
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' },
            ],
          })
        }

        if (panelOrientationsInSystem.portrait) {
          metasoleTextFieldInputs.push({
            label: 'Roof rib distance',
            variableName: 'roofRibDistance',
            default: 250,
            type: 'number',
            min: 10,
            max: 370,
          })
        }

        return {
          dropdownInputs: [...metasoleDropDownInputs],
          textFieldInputs: [...metasoleTextFieldInputs],
        }
      },
    },
    gseInRoof: {
      class: GseInRoof,
      manufacturer: 'GSE Integration',
      getInputs: (projectForm, system) => {
        const items = [
          {
            label: 'Clamps per panel',
            variableName: 'clampQty',
            default: '6 or 8',
            options: [
              { label: '4', value: '4' },
              { label: '6 or 8 (determined by frame)', value: '6 or 8' },
            ],
          },
          {
            label: 'Top Connection',
            variableName: 'topConnection',
            default: 'flexalu',
            options: [
              { label: 'Top Flashing', value: 'flashing' },
              { label: 'Flexible Strip', value: 'flexalu' },
            ],
          },
          {
            label: 'Flexible Strip',
            variableName: 'flexibleStrip',
            default: 330,
            options: [
              { label: 'Flexible Sealing Strip 330mm x 5m', value: 330 },
              { label: 'Flexible Sealing Strip 500mm x 5m', value: 500 },
            ],
          },
          {
            label: 'Does the module have a rear support bar?',
            variableName: 'rearSupportBarIncluded',
            default: false,
            options: [
              { label: 'Yes', value: true },
              { label: 'No', value: false },
            ],
          },
        ] as DropdownInput[]

        return {
          dropdownInputs: [...items],
          textFieldInputs: [],
        }
      },
    },
    k2OnRoof: {
      class: K2OnRoof,
      manufacturer: 'K2 Systems',
      getInputs: createK2Inputs('k2OnRoof', roofTypeName),
    },
    k2SimpleTilt: {
      class: K2SimpleTilt,
      manufacturer: 'K2 Systems',
      getInputs: createK2Inputs('k2SimpleTilt', roofTypeName),
    },
    k2GroundMount: {
      class: K2GroundMount,
      manufacturer: 'K2 Systems',
      getInputs: createK2Inputs('k2GroundMount', roofTypeName),
    },
    k2OnRoofUK: {
      class: K2OnRoofUK,
      manufacturer: 'K2 Systems',
      getInputs: createK2UKInputs(roofTypeName),
    },
    solarPortGroundMount: {
      class: SolarPortGroundMount,
      manufacturer: 'Solarport Systems',
      getInputs: createSolarPortInputs(),
    },
    nova: {
      class: Nova,
      manufacturer: 'NOVA',
      getInputs: createNovaInputs(),
    },
    vanDerValk: {
      class: VanDerValk,
      manufacturer: 'Valk Solar System',
      getInputs: (_projectForm, system) => {
        const windCache = getDlubalUKWindCache()
        const windDefault =
          windCache?.success && windCache.value != null && Number.isFinite(windCache.value) ? windCache.value : ''

        const distanceCache = getDlubalUKDistanceToSeaCache()
        const distanceDefault =
          distanceCache?.success && distanceCache.value != null && Number.isFinite(distanceCache.value)
            ? distanceCache.value
            : ''

        const vdv = (parseIntegrationJson(system.integration_json) as { vanDerValk?: Record<string, unknown> } | null)
          ?.vanDerValk
        const rawSel = vdv?.selectedSuitability
        let middleZoneDeflectorDropdown: DropdownInput | null = null
        const solarRampDropdowns: DropdownInput[] = []
        if (rawSel != null && typeof rawSel === 'object' && isVanDerValkSuitabilityConfigRow(rawSel)) {
          const row = rawSel as VanDerValkSuitabilityConfig
          if (isVanDerValkSolarRampSystem(row.system)) {
            middleZoneDeflectorDropdown = {
              label: 'mount_vdv_middle_zone_deflector',
              variableName: 'middleZoneDeflector',
              default: 'OPEN_SYSTEM',
              options: SOLAR_RAMP_MIDDLE_ZONE_DEFLECTOR_OPTIONS.map((value) => ({
                label:
                  value === 'OPEN_SYSTEM'
                    ? 'mount_vdv_middle_zone_open_system'
                    : 'mount_vdv_middle_zone_closed_system',
                value,
              })),
            }
            const profile = getSolarRampProfile(row.system)
            if (profile) {
              solarRampDropdowns.push({
                label: 'mount_vdv_solar_ramp_foundation_type',
                variableName: 'foundationType',
                default: DEFAULT_SOLAR_RAMP_FOUNDATION_TYPE,
                options: profile.foundationTypes.map((ft) => ({
                  label: getSolarRampFoundationTypeLabelKey(ft),
                  value: ft,
                })),
              })
              solarRampDropdowns.push({
                label: 'mount_vdv_solar_ramp_fixation',
                variableName: 'flatRoofFixation',
                default: DEFAULT_SOLAR_RAMP_FIXATION,
                options: SOLAR_RAMP_FIXATION_OPTIONS,
              })
              const currentFixation = vdv?.flatRoofFixation === 'ANCHORING' ? 'ANCHORING' : 'BALLASTING'
              if (currentFixation === 'ANCHORING') {
                solarRampDropdowns.push({
                  label: 'mount_vdv_anchor_type',
                  variableName: 'anchorType',
                  default: 'VALK_SOLAR_FIX',
                  options: SOLAR_RAMP_ANCHOR_TYPE_OPTIONS,
                })
                const anchorType = vdv?.anchorType === 'OTHER' ? 'OTHER' : 'VALK_SOLAR_FIX'
                if (anchorType === 'VALK_SOLAR_FIX') {
                  const roofMaterial = typeof vdv?.roofMaterial === 'string' ? vdv.roofMaterial : undefined
                  const fastenerType = typeof vdv?.fastenerType === 'string' ? vdv.fastenerType : undefined
                  const materialOptions = getSolarRampRoofMaterialOptions(fastenerType)
                  const fastenerOptions = getSolarRampFastenerTypeOptions(roofMaterial)
                  const heightOptions = getSolarRampRoofConstructionHeightOptions(roofMaterial, fastenerType)
                  solarRampDropdowns.push({
                    label: 'mount_vdv_roof_material',
                    variableName: 'roofMaterial',
                    default: materialOptions[0] ?? '',
                    options: materialOptions.map((value) => ({
                      label: getSolarRampRoofMaterialLabelKey(value),
                      value,
                    })),
                  })
                  solarRampDropdowns.push({
                    label: 'mount_vdv_fastener_type',
                    variableName: 'fastenerType',
                    default: fastenerOptions[0] ?? '',
                    options: fastenerOptions.map((value) => ({
                      label: getSolarRampFastenerTypeLabelKey(value),
                      value,
                    })),
                  })
                  solarRampDropdowns.push({
                    label: 'mount_vdv_roof_construction_height',
                    variableName: 'roofConstructionHeight',
                    default: heightOptions[0] != null ? String(heightOptions[0]) : '',
                    options: heightOptions.map((value) => ({
                      label: getSolarRampRoofConstructionHeightLabel(value),
                      value: String(value),
                    })),
                  })
                }
              }
            }
          } else {
          const place = String(row.placeSystem ?? '')
            .trim()
            .toUpperCase()
          const layout = parseVanDerValkModuleLayout(row.moduleOrientation)
          if (place === 'NS' && layout === 'portrait') {
            middleZoneDeflectorDropdown = {
              label: 'mount_vdv_middle_zone_deflector',
              variableName: 'middleZoneDeflector',
              default: 'CLOSED_SYSTEM',
              options: [{ label: 'mount_vdv_middle_zone_closed_system', value: 'CLOSED_SYSTEM' }],
            }
          } else if (place === 'NS' && layout === 'landscape') {
            middleZoneDeflectorDropdown = {
              label: 'mount_vdv_middle_zone_deflector',
              variableName: 'middleZoneDeflector',
              default: 'ONLY_BACKPLATES',
              options: [
                { label: 'mount_vdv_middle_zone_only_backplates', value: 'ONLY_BACKPLATES' },
                { label: 'mount_vdv_middle_zone_closed_system', value: 'CLOSED_SYSTEM' },
              ],
            }
          } else if (place === 'EW' && layout === 'landscape') {
            middleZoneDeflectorDropdown = {
              label: 'mount_vdv_middle_zone_deflector',
              variableName: 'middleZoneDeflector',
              default: 'OPEN_SYSTEM',
              options: [
                { label: 'mount_vdv_middle_zone_open_system', value: 'OPEN_SYSTEM' },
                { label: 'mount_vdv_middle_zone_closed_system', value: 'CLOSED_SYSTEM' },
              ],
            }
          } else if (place === 'EW' && layout === 'portrait') {
            middleZoneDeflectorDropdown = {
              label: 'mount_vdv_middle_zone_deflector',
              variableName: 'middleZoneDeflector',
              default: 'CLOSED_SYSTEM',
              options: [{ label: 'mount_vdv_middle_zone_closed_system', value: 'CLOSED_SYSTEM' }],
            }
          }
          }
        }

        const topographyTypeOptions = [
          { label: 'mount_vdv_topography_not_exposed', value: 'Not exposed' },
          { label: 'mount_vdv_topography_hills_or_ridge', value: 'Hills or Ridge' },
          { label: 'mount_vdv_topography_escarpments', value: 'Escarpments or Cliffs' },
        ]
        const validTopographyTypes = new Set(topographyTypeOptions.map((o) => o.value))
        const currentTopographyType =
          typeof vdv?.topography_type === 'string' && validTopographyTypes.has(vdv.topography_type)
            ? (vdv.topography_type as string)
            : 'Not exposed'

        const slopeOptions = [
          { label: 'mount_vdv_topography_slope_10', value: '10%' },
          { label: 'mount_vdv_topography_slope_20', value: '20%' },
          { label: 'mount_vdv_topography_slope_gt30', value: '>30%' },
        ]
        const validSlopes = new Set(slopeOptions.map((o) => o.value))
        const currentSlope =
          typeof vdv?.topography_slope === 'string' && validSlopes.has(vdv.topography_slope)
            ? (vdv.topography_slope as string)
            : '10%'
        const currentZone =
          typeof vdv?.topography_zone === 'string' && (vdv.topography_zone === 'A' || vdv.topography_zone === 'B')
            ? (vdv.topography_zone as string)
            : 'A'

        const topographyDropdowns: DropdownInput[] = [
          {
            label: 'mount_vdv_topography_type',
            variableName: 'topography_type',
            default: currentTopographyType,
            options: topographyTypeOptions,
          },
        ]
        if (currentTopographyType === 'Escarpments or Cliffs') {
          topographyDropdowns.push(
            {
              label: 'mount_vdv_topography_zone',
              variableName: 'topography_zone',
              default: currentZone,
              options: [
                { label: 'mount_vdv_topography_zone_a', value: 'A' },
                { label: 'mount_vdv_topography_zone_b', value: 'B' },
              ],
            },
            {
              label: 'mount_vdv_topography_slope',
              variableName: 'topography_slope',
              default: currentSlope,
              options: slopeOptions,
            }
          )
        } else if (currentTopographyType === 'Hills or Ridge') {
          topographyDropdowns.push({
            label: 'mount_vdv_topography_slope',
            variableName: 'topography_slope',
            default: currentSlope,
            options: slopeOptions,
          })
        }

        const isPitchedRoof = system.mounting_type === 'onRoof'

        return {
          dropdownInputs: [
            ...topographyDropdowns,
            {
              label: 'mount_vdv_consequence_class',
              variableName: 'kFIIdx',
              default: '0',
              options: [
                { label: 'mount_vdv_consequence_class_cc1', value: '0' },
                { label: 'mount_vdv_consequence_class_cc2', value: '1' },
                { label: 'mount_vdv_consequence_class_cc3', value: '2' },
              ],
            },
            {
              label: 'mount_vdv_terrain_category',
              variableName: 'terrainCategoryIdx',
              default: '0',
              options: [
                { label: 'mount_vdv_terrain_sea', value: '0' },
                { label: 'mount_vdv_terrain_country', value: '1' },
                { label: 'mount_vdv_terrain_town', value: '2' },
              ],
            },
            ...(middleZoneDeflectorDropdown ? [middleZoneDeflectorDropdown] : []),
            ...solarRampDropdowns,
          ],
          textFieldInputs: isPitchedRoof
            ? []
            : [
                {
                  label: 'mount_vdv_parapet_height',
                  variableName: 'parapetHeightM',
                  type: 'number',
                  default: 1,
                  min: 0,
                  max: 50,
                },
              ],
          trailingDropdownInputs: [
            {
              label: 'mount_vdv_snow_zone',
              variableName: 'snowZone',
              default: 0,
              options: [
                { label: 'mount_vdv_snow_zone_1', value: 0 },
                { label: 'mount_vdv_snow_zone_2', value: 1 },
                { label: 'mount_vdv_snow_zone_3', value: 2 },
                { label: 'mount_vdv_snow_zone_4', value: 3 },
                { label: 'mount_vdv_snow_zone_5', value: 4 },
                { label: 'mount_vdv_snow_zone_6_5', value: 5 },
              ],
              button: createDlubalUKSnowZoneButtonForVanDerValk(['vanDerValk']),
            },
          ],
          trailingTextFieldInputs: [
            {
              label: 'mount_vdv_basic_wind_speed',
              variableName: 'windSpeedMs',
              type: 'number',
              default: windDefault,
              button: createDlubalUKWindSpeedMsButton(['vanDerValk']),
            },
            {
              label: 'mount_vdv_distance_to_shoreline',
              variableName: 'distanceToShorelineKM',
              type: 'number',
              default: distanceDefault,
              min: 0,
              max: 10000,
              button: createDlubalUKDistanceToShorelineButton(['vanDerValk']),
            },
            {
              label: 'mount_vdv_altitude_above_sea',
              variableName: 'altitudeAboveSea',
              type: 'number',
              default: '',
              button: createDlubalUKSnowElevationAltitudeButton(['vanDerValk']),
            },
          ],
        }
      },
    },
    fastRack: {
      class: FastRack,
      manufacturer: 'Fast-Rack',
      getInputs: createFastRackInputs(),
      postProcessMergedItems: FastRack.applySystemWidePostProcessing,
    },
    snapnrackUltraRail: {
      class: SnapNRackUltraRail,
      manufacturer: 'SnapNrack',
      getInputs: createSnapnrackInputs(),
    },
  }
}

export default getNativeMountingSystems
